/**
 * Product Form V2 - JavaScript avec BEM
 * Nouvelle logique de synchronisation variantes/selling plans
 */

class ProductFormV2 {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.form = container.querySelector('form[action*="/cart/add"]');

    if (!this.form) return;

    // Elements du formulaire
    this.submitButton = container.querySelector('.product-form__submit');
    this.variantInput = this.form.querySelector('input[name="id"]');
    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
    this.errorMessage = container.querySelector('[data-error-message]');

    // Nouveaux sélecteurs pour V2
    this.variantRadios = container.querySelectorAll('[name^="variant-"]');
    this.frequencyRadios = container.querySelectorAll('[name^="purchase-type-"]');
    this.sellingPlanRadios = container.querySelectorAll('[name^="selling-plan-"]');
    this.frequencyOptions = container.querySelectorAll('.product-form__frequency-option');

    // Éléments de prix et textes dynamiques
    this.oneTimePriceDisplay = container.querySelector('[data-one-time-price]');
    this.oneTimeComparePriceDisplay = container.querySelector('[data-one-time-compare-price]');
    this.oneTimeBadge = container.querySelector('[data-one-time-badge]');
    this.subscriptionPriceDisplay = container.querySelector('[data-subscription-price]');
    this.subscriptionComparePriceDisplay = container.querySelector('[data-subscription-compare-price]');
    this.subscriptionBadge = container.querySelector('[data-subscription-badge]');
    this.subscriptionTitle = container.querySelector('[data-subscription-title]');
    this.deliveryNote = container.querySelector('[data-delivery-note]');
    this.buttonPriceSpan = this.submitButton?.querySelector('[data-button-price]');
    this.buttonComparePriceSpan = this.submitButton?.querySelector('[data-button-compare-price]');

    // Bannière Opération Promotionnelle (chercher dans le container parent)
    this.opePromoBanner = container.querySelector('[data-ope-promo-banner]');
    this.customBannerBlock = container.querySelector('.product-banner__custom_block');

    // Paramètres Sova Days
    this.sovaDaysEnabled = container.dataset.sovaDaysEnabled === 'true';
    this.sovaDaysDiscount = parseFloat(container.dataset.sovaDaysDiscount) || 0;
    this.sovaDaysAdditionalAmount = parseFloat(container.dataset.sovaDaysAdditionalAmount) || 0;
    this.subscriptionMessage = container.dataset.subscriptionMessage || '';
    this.sovaDaysBadge = container.querySelector('[data-sova-days-badge]');

    // Paramètres Opération Promotionnelle
    this.opePromoEnabled = container.dataset.opePromoEnabled === 'true';

    // Paramètre One-Time par défaut (depuis product metafield)
    this.productOneTimeByDefault = container.dataset.productOneTimeDefault === 'true';

    // Paramètres badge format
    this.subscriptionBadgeFormat = container.dataset.subscriptionBadgeFormat || 'percentage';
    this.savingsText = container.dataset.savingsText || 'économisés';

    // Sticky Button Elements
    // Recherche limitée au conteneur : la page rend deux formulaires produit
    // (la fiche et le tiroir d'achat rapide), chacun avec son propre CTA
    // sticky. Un `document.querySelector` renvoyait celui du tiroir, caché,
    // et laissait celui de la fiche sans observateur ni mise à jour de prix.
    this.stickyCtaContainer = container.querySelector('[data-sticky-cta]');
    this.stickySubmitButton = container.querySelector('[data-sticky-submit]');
    this.stickyLabel = container.querySelector('[data-sticky-label]');
    this.stickyPrice = container.querySelector('[data-sticky-price]');
    this.stickyComparePrice = container.querySelector('[data-sticky-compare-price]');

    // Frequency Details avec [current_reduction]
    this.frequencyDetailItems = container.querySelectorAll('.product-form__frequency-detail-item');

    // Description produit/variante
    this.descriptionContainer = container.querySelector('[data-product-description]');

    // Protocol header elements
    this.protocolHeader = container.querySelector('[data-protocol-header]');
    this.protocolItemsWrapper = container.querySelector('[data-protocol-items-wrapper]');
    this.protocolPrice = container.querySelector('[data-protocol-price]');
    this.protocolComparePrice = container.querySelector('[data-protocol-compare-price]');
    this.protocolDiscount = container.querySelector('[data-protocol-discount]');
    this.protocolPriceDetails = container.querySelector('[data-protocol-price-details]');
    this.protocolSavingsBadge = container.querySelector('[data-protocol-savings-badge]');
    this.protocolSavingsAmount = container.querySelector('[data-protocol-savings-amount]');
    this.protocolDiscountPrefix = container.querySelector('[data-protocol-discount]')?.textContent.split('-')[0]?.trim() || 'SOIT';

    this.init();
  }

  init() {
    this.bindEvents();
    this.handleVariantChange(); // Initialiser avec la variante sélectionnée
    this.replacePricePlaceholder();
    this.initStickyButton();
  }

  bindEvents() {
    // Changement de variante (Durée de la cure)
    this.variantRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleVariantChange());
    });

    // Changement de type d'achat (Achat unique / Abonnement)
    this.frequencyRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleFrequencyChange());
    });

    // Soumission du formulaire
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  /**
   * Gestion du changement de variante
   * Logique: première variante → premier selling plan, sinon → deuxième selling plan
   */
  handleVariantChange() {
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    if (!selectedVariant) return;

    const variantId = selectedVariant.dataset.variantId;
    const variantIndex = parseInt(selectedVariant.dataset.variantIndex);
    const variantPrice = parseInt(selectedVariant.dataset.variantPrice);
    const variantComparePrice = parseInt(selectedVariant.dataset.variantComparePrice) || 0;

    // Mettre à jour l'input hidden de variante
    if (this.variantInput && variantId) {
      this.variantInput.value = variantId;
    }

    // Quantité toujours à 1
    if (this.quantityInput) {
      this.quantityInput.value = 1;
    }

    // Toujours mettre à jour les prix d'achat unique
    this.updateOneTimePriceDisplay(variantPrice, variantComparePrice);

    // Mettre à jour la bannière Opération Promotionnelle
    this.updateOpePromoBanner(selectedVariant);

    // Description : affichage fixe depuis le metafield produit (pas de changement par variante)
    // this.updateDescription(selectedVariant);

    // Gérer le type d'achat par défaut selon le metafield de la variante
    this.updatePurchaseTypeDefault(selectedVariant);

    // Mettre à jour les textes avec [current_reduction]
    this.updateFrequencyDetails(selectedVariant);

    // Gérer les selling plans s'ils existent
    const sellingPlans = Array.from(this.sellingPlanRadios);

    if (sellingPlans.length > 0) {
      // Déterminer quel selling plan sélectionner
      // Si première variante (index 0) → premier plan, sinon → deuxième plan
      const sellingPlanIndex = variantIndex === 0 ? 0 : 1;

      if (sellingPlans[sellingPlanIndex]) {
        sellingPlans[sellingPlanIndex].checked = true;

        // Récupérer les données du selling plan sélectionné
        const selectedPlan = sellingPlans[sellingPlanIndex];
        const discountPercent = parseInt(selectedPlan.dataset.discountPercent) || 0;
        const planName = selectedPlan.dataset.sellingPlanName || '';

        // Remise de l'abonnement de la durée sélectionnée : [subscription_reduction]
        this.subscriptionReduction = discountPercent;
        this.updateFrequencyDetails(selectedVariant);

        // IMPORTANT : Appliquer d'abord les réductions variantes/sova days sur le prix de la variante
        // (comme en Liquid : one_time_price = prix avec réductions, puis subscription_price = one_time_price avec réduction selling plan)
        const oneTimePrice = this.applySovaDaysDiscount(variantPrice);

        // Calculer le prix subscription à partir du prix déjà réduit
        const subscriptionPrice = Math.round(oneTimePrice * (1 - discountPercent / 100));

        // Mettre à jour les prix d'abonnement (sans réappliquer les réductions dans updateSubscriptionDisplay)
        this.updateSubscriptionDisplay(variantPrice, variantComparePrice, subscriptionPrice, discountPercent, planName, true);

        // Mettre à jour le selling_plan input si abonnement est sélectionné
        const isSubscriptionSelected = this.container.querySelector('[name^="purchase-type-"]:checked')?.value === 'subscription';
        if (isSubscriptionSelected && this.sellingPlanInput) {
          this.sellingPlanInput.value = selectedPlan.dataset.sellingPlanId;
        }
      }
    }

    // Mettre à jour le protocol header (items + prix)
    this.updateProtocolDisplay(variantId, variantPrice, variantComparePrice);

    // Mettre à jour le prix du bouton
    this.updateButtonPrice();
  }

  /**
   * Gestion du changement de type d'achat (Achat unique / Abonnement)
   */
  handleFrequencyChange() {
    const selectedFrequency = this.container.querySelector('[name^="purchase-type-"]:checked');
    if (!selectedFrequency) return;

    const purchaseType = selectedFrequency.value;

    // Gérer les classes --selected sur les options
    this.frequencyOptions.forEach(option => {
      if (option.dataset.option === purchaseType) {
        option.classList.add('product-form__frequency-option--selected');
      } else {
        option.classList.remove('product-form__frequency-option--selected');
      }
    });

    // Mettre à jour le selling_plan input
    if (purchaseType === 'one-time') {
      // Achat unique : vider le selling_plan
      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }
    } else if (purchaseType === 'subscription') {
      // Abonnement : mettre le selling_plan sélectionné
      const selectedPlan = this.container.querySelector('[name^="selling-plan-"]:checked');
      if (selectedPlan && this.sellingPlanInput) {
        this.sellingPlanInput.value = selectedPlan.dataset.sellingPlanId;
      }
    }

    this.refreshProtocolPrice();
    this.updateButtonPrice();
  }

  /**
   * Met à jour le type d'achat par défaut selon le metafield du produit
   * - custom.page_produit_one_time_by_default === true : force ACHAT UNIQUE (one-time)
   * - sinon : force SUBSCRIPTION
   */
  updatePurchaseTypeDefault(selectedVariant) {
    if (!selectedVariant) return;

    if (this.productOneTimeByDefault) {
      // Force ONE-TIME when product metafield is true
      const oneTimeRadio = this.container.querySelector('[name^="purchase-type-"][value="one-time"]');
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
        this.handleFrequencyChange();
      }
    } else {
      // Force SUBSCRIPTION when product metafield is false
      const subscriptionRadio = this.container.querySelector('[name^="purchase-type-"][value="subscription"]');
      if (subscriptionRadio) {
        subscriptionRadio.checked = true;
        this.handleFrequencyChange();
      }
    }
  }

  /**
   * Met à jour les textes avec [current_reduction] selon la variante sélectionnée
   */
  updateFrequencyDetails(selectedVariant) {
    if (!selectedVariant || this.frequencyDetailItems.length === 0) return;

    // Calculer la réduction courante (priorité: ope_promo > sova_days > compare_at)
    let currentReduction = 0;
    let hasReduction = false;

    // Priorité 1: Opération Promotionnelle (avec système de paliers)
    const opePromoDiscount = this.getVariantOpePromoDiscount();
    if (this.opePromoEnabled && opePromoDiscount > 0) {
      currentReduction = opePromoDiscount;
      hasReduction = true;
    }

    // Priorité 2: Sova Days (si pas d'ope_promo)
    if (!hasReduction && this.sovaDaysEnabled) {
      const totalSovaDays = this.sovaDaysDiscount + this.sovaDaysAdditionalAmount;
      if (totalSovaDays > 0) {
        currentReduction = totalSovaDays;
        hasReduction = true;
      }
    }

    // Priorité 3: Compare at price (si pas d'ope_promo ni sova_days)
    if (!hasReduction) {
      const variantPrice = parseInt(selectedVariant.dataset.variantPrice) || 0;
      const variantComparePrice = parseInt(selectedVariant.dataset.variantComparePrice) || 0;
      if (variantComparePrice > variantPrice) {
        const savings = variantComparePrice - variantPrice;
        currentReduction = Math.round((savings / variantComparePrice) * 100);
        hasReduction = true;
      }
    }

    console.log('=== UPDATE FREQUENCY DETAILS ===');
    console.log('currentReduction:', currentReduction);
    console.log('hasReduction:', hasReduction);

    // Mettre à jour chaque item
    this.frequencyDetailItems.forEach((item, index) => {
      const paragraph = item.querySelector('p');
      const hasPlaceholder = item.dataset.hasPlaceholder === 'true';
      const originalText = item.dataset.originalText;

      console.log(`Item ${index}:`, {
        hasPlaceholder,
        originalText,
        hasReduction
      });

      if (paragraph && originalText) {
        // Si le texte contient [current_reduction] et qu'il n'y a pas de réduction, cacher l'item
        if (hasPlaceholder && !hasReduction) {
          console.log(`  → Cacher item ${index} (placeholder sans réduction)`);
          item.style.display = 'none';
        } else {
          item.style.display = '';
          // Remplacer [current_reduction] et [subscription_reduction] par leurs valeurs
          const finalText = originalText
            .replace('[current_reduction]', currentReduction)
            .replace('[subscription_reduction]', this.subscriptionReduction ?? '');
          paragraph.textContent = finalText;
          console.log(`  → Afficher item ${index}: "${finalText}"`);
        }
      }
    });
    console.log('================================');
  }

  /**
   * Met à jour la bannière Opération Promotionnelle selon la variante sélectionnée
   * Affiche/cache le bloc parent .product-banner__custom_block selon si la variante a une promo
   * Met à jour dynamiquement le contenu de la bannière (titre, description, icône)
   * Si la bannière est au niveau produit (data-product-level-promo="true"), le contenu ne change pas
   */
  updateOpePromoBanner(selectedVariant) {
    if (!this.customBannerBlock) {
      return;
    }

    // Si la bannière est au niveau produit, ne pas mettre à jour le contenu (il reste fixe)
    const isProductLevelPromo = this.opePromoBanner?.dataset.productLevelPromo === 'true';
    if (isProductLevelPromo) {
      // La bannière reste toujours visible avec son contenu initial
      this.customBannerBlock.style.display = '';
      this.opePromoBanner.classList.remove('product-banner__ope-promo--hidden');
      return;
    }

    const variantHasBannerContent = selectedVariant?.dataset.variantHasBannerContent === 'true';

    if (variantHasBannerContent && this.opePromoBanner) {
      // Récupérer les données de la bannière depuis les data attributes
      const bannerTitle = selectedVariant.dataset.variantBannerTitle || '';
      const bannerDescription = selectedVariant.dataset.variantBannerDescription || '';
      const bannerIconUrl = selectedVariant.dataset.variantBannerIcon || '';

      // Mettre à jour le titre (le HTML vient déjà formaté par metafield_tag)
      const titleElement = this.opePromoBanner.querySelector('.product-banner__ope-promo-title');
      if (titleElement) {
        if (bannerTitle) {
          titleElement.innerHTML = bannerTitle;
          titleElement.style.display = '';
        } else {
          titleElement.style.display = 'none';
        }
      }

      // Mettre à jour la description (le HTML vient déjà formaté par metafield_tag)
      const descriptionElement = this.opePromoBanner.querySelector('.product-banner__ope-promo-description');
      if (descriptionElement) {
        if (bannerDescription) {
          descriptionElement.innerHTML = bannerDescription;
          descriptionElement.style.display = '';
        } else {
          descriptionElement.style.display = 'none';
        }
      }

      // Mettre à jour l'icône
      const iconContainer = this.opePromoBanner.querySelector('.product-banner__ope-promo-icon');
      if (iconContainer) {
        if (bannerIconUrl) {
          iconContainer.innerHTML = `<img src="${bannerIconUrl}" alt="" loading="lazy">`;
          iconContainer.style.display = '';
        } else {
          iconContainer.style.display = 'none';
        }
      }

      // Afficher le bloc parent et la bannière ope_promo
      this.customBannerBlock.style.display = '';
      this.opePromoBanner.classList.remove('product-banner__ope-promo--hidden');
    } else {
      // Cacher la bannière ope_promo si pas de contenu
      if (this.opePromoBanner) {
        this.opePromoBanner.classList.add('product-banner__ope-promo--hidden');
      }

      // Vérifier si d'autres bannières sont visibles avant de cacher le bloc complet
      const otherVisibleBanners = this.customBannerBlock.querySelector('.product-banner__sova-days, .product-banner__sova-days-preco');
      if (!otherVisibleBanners) {
        this.customBannerBlock.style.display = 'none';
      }
    }
  }

  /**
   * Met à jour la description selon la variante sélectionnée
   * Affiche la description de la variante si remplie, sinon celle du produit
   */
  updateDescription(selectedVariant) {
    if (!this.descriptionContainer) return;

    // Récupérer la description de la variante depuis le data attribute
    const variantDescription = selectedVariant?.dataset.variantDescriptionShort || '';

    // Récupérer la description du produit (fallback)
    const productDescriptionHtml = this.descriptionContainer.dataset.productDescriptionHtml || '';

    if (variantDescription) {
      // Afficher la description de la variante (texte simple dans un <p>)
      this.descriptionContainer.innerHTML = `<p>${variantDescription}</p>`;
    } else if (productDescriptionHtml) {
      // Afficher la description du produit (HTML richtext)
      this.descriptionContainer.innerHTML = productDescriptionHtml;
    }
  }

  /**
   * Met à jour l'affichage du prix d'achat unique
   * Affiche le prix d'origine le plus bas (compare_price ou prix avant réduction)
   */
  updateOneTimePriceDisplay(price, comparePrice) {
    // Appliquer les réductions (Opération Promotionnelle ou Sova Days)
    const discountedPrice = this.applySovaDaysDiscount(price);
    const hasDiscount = discountedPrice < price;
    const sovaDaysReductionPrice = this.getVariantSovaDaysReductionPrice();
    const hasSovaDaysFixedPrice = sovaDaysReductionPrice > 0;
    const variantOpePromoDiscount = this.getVariantOpePromoDiscount();
    const hasOpePromoDiscount = this.opePromoEnabled && variantOpePromoDiscount > 0;
    const hasSovaDaysDiscount = this.sovaDaysEnabled && this.sovaDaysDiscount > 0 && !hasOpePromoDiscount;

    // Prix final (toujours affiché)
    if (this.oneTimePriceDisplay && price) {
      this.oneTimePriceDisplay.textContent = this.formatPrice(discountedPrice);
      // Couleur #985bc8 si sova_days_reduction_price est set
      this.oneTimePriceDisplay.style.color = hasSovaDaysFixedPrice ? '#985bc8' : '';
    }

    // Prix barré : si sova_days_reduction_price → barrer le price (pas compare_at_price)
    if (this.oneTimeComparePriceDisplay) {
      if (hasSovaDaysFixedPrice) {
        this.oneTimeComparePriceDisplay.textContent = this.formatPrice(price);
        this.oneTimeComparePriceDisplay.style.display = '';
      } else if (comparePrice && comparePrice > 0 && comparePrice > discountedPrice) {
        this.oneTimeComparePriceDisplay.textContent = this.formatPrice(comparePrice);
        this.oneTimeComparePriceDisplay.style.display = '';
      } else if (hasDiscount) {
        this.oneTimeComparePriceDisplay.textContent = this.formatPrice(price);
        this.oneTimeComparePriceDisplay.style.display = '';
      } else {
        this.oneTimeComparePriceDisplay.style.display = 'none';
      }
    }

    // Badge de réduction pour achat unique
    if (this.oneTimeBadge) {
      let savingsAmount = 0;
      let savingsPercent = 0;
      let hasSavings = false;

      // Calculer les économies par rapport au compare_price ou au prix avant réduction
      if (comparePrice && comparePrice > 0 && comparePrice > discountedPrice) {
        savingsAmount = comparePrice - discountedPrice;
        savingsPercent = Math.round((savingsAmount / comparePrice) * 100);
        hasSavings = true;
      } else if (hasDiscount) {
        savingsAmount = price - discountedPrice;
        savingsPercent = Math.round((savingsAmount / price) * 100);
        hasSavings = true;
      }

      if (hasSavings && savingsPercent > 0) {
        // Afficher le badge avec le format choisi (percentage ou money)
        if (this.subscriptionBadgeFormat === 'money') {
          this.oneTimeBadge.textContent = `${this.formatPrice(savingsAmount)} ${this.savingsText}`;
        } else {
          this.oneTimeBadge.textContent = `-${savingsPercent}%`;
        }
        this.oneTimeBadge.style.display = '';
      } else {
        // Pas d'économies, cacher le badge
        this.oneTimeBadge.style.display = 'none';
      }
    }
  }

  /**
   * Met à jour l'affichage de l'abonnement
   * Affiche le prix d'origine le plus bas (compare_price ou prix avant réduction)
   * @param {boolean} alreadyDiscounted - Si true, subscriptionPrice a déjà les réductions appliquées
   */
  updateSubscriptionDisplay(variantPrice, variantComparePrice, subscriptionPrice, discountPercent, planName, alreadyDiscounted = false) {
    // Si les réductions sont déjà appliquées, ne pas les réappliquer
    const discountedSubscriptionPrice = alreadyDiscounted ? subscriptionPrice : this.applySovaDaysDiscount(subscriptionPrice);

    // Calculer le prix d'abonnement SANS réductions variantes pour l'affichage du prix barré
    const subscriptionPriceWithoutVariantDiscount = Math.round(variantPrice * (1 - discountPercent / 100));
    const hasDiscount = discountedSubscriptionPrice < subscriptionPriceWithoutVariantDiscount;
    const variantOpePromoDiscount = this.getVariantOpePromoDiscount();
    const hasOpePromoDiscount = this.opePromoEnabled && variantOpePromoDiscount > 0;
    const hasSovaDaysDiscount = this.sovaDaysEnabled && this.sovaDaysDiscount > 0 && !hasOpePromoDiscount;

    // Prix final d'abonnement (toujours affiché)
    if (this.subscriptionPriceDisplay) {
      this.subscriptionPriceDisplay.textContent = this.formatPrice(discountedSubscriptionPrice);
    }

    // Prix d'origine le plus bas (compare_price ou prix d'abonnement avant réduction)
    if (this.subscriptionComparePriceDisplay) {
      if (variantComparePrice && variantComparePrice > 0 && variantComparePrice > discountedSubscriptionPrice) {
        // Afficher compare_price s'il existe et est supérieur au prix final
        this.subscriptionComparePriceDisplay.textContent = this.formatPrice(variantComparePrice);
        this.subscriptionComparePriceDisplay.style.display = '';
      } else if (hasDiscount) {
        // Sinon, afficher le prix d'abonnement avant réduction (Opération Promotionnelle ou Sova Days)
        this.subscriptionComparePriceDisplay.textContent = this.formatPrice(subscriptionPriceWithoutVariantDiscount);
        this.subscriptionComparePriceDisplay.style.display = '';
      } else {
        this.subscriptionComparePriceDisplay.style.display = 'none';
      }
    }

    // Badge de réduction
    let totalDiscountPercent = discountPercent;
    let totalDiscountAmount = 0;
    if (this.subscriptionBadge) {
      // Calculer le pourcentage de réduction par rapport au compare_price (si existe) ou au variantPrice
      const basePrice = variantComparePrice && variantComparePrice > 0 ? variantComparePrice : variantPrice;
      totalDiscountPercent = Math.round((1 - (discountedSubscriptionPrice / basePrice)) * 100);
      totalDiscountAmount = basePrice - discountedSubscriptionPrice;

      if (totalDiscountPercent > 0) {
        if (this.subscriptionBadgeFormat === 'money') {
          this.subscriptionBadge.textContent = `${this.formatPrice(totalDiscountAmount)} ${this.savingsText}`;
        } else {
          this.subscriptionBadge.textContent = `-${totalDiscountPercent}%`;
        }
      }
    }

    // Titre de l'abonnement (nom du plan)
    if (this.subscriptionTitle && planName) {
      // Sauvegarder l'icône si elle existe
      const infoIcon = this.subscriptionTitle.querySelector('.product-form__frequency-info-icon');

      // Mettre à jour le texte
      this.subscriptionTitle.textContent = planName;

      // Réajouter l'icône si elle existait
      if (infoIcon) {
        this.subscriptionTitle.appendChild(infoIcon);
      }
    }

    // Mettre à jour le badge Sova Days avec les bons pourcentages
    // Premier pourcentage = totalDiscountPercent (affiché dans le badge)
    // Deuxième pourcentage = discountPercent (réduction de base de la subscription)
    // Badge Sova Days est TOUJOURS en pourcentage
    this.updateSovaDaysBadge(totalDiscountPercent, discountPercent);
  }

  /**
   * Met à jour le badge Sova Days (TOUJOURS EN POURCENTAGE)
   * @param {number} totalDiscountPercent - Pourcentage total affiché dans le badge (incluant Sova Days)
   * @param {number} baseDiscountPercent - Pourcentage de réduction de base de la subscription
   */
  updateSovaDaysBadge(totalDiscountPercent, baseDiscountPercent) {
    if (!this.sovaDaysBadge || !this.subscriptionMessage || !this.sovaDaysEnabled) {
      return;
    }

    // Remplacer [reduction] par le pourcentage total (celui du badge)
    let message = this.subscriptionMessage.replace('[reduction]', totalDiscountPercent);

    // Remplacer -10% par le pourcentage de base de la subscription
    const discountText = `-${baseDiscountPercent}%`;
    message = message.replace('-10%', discountText);

    // Mettre à jour le badge
    this.sovaDaysBadge.textContent = message;
  }

  /**
   * Met à jour le prix dans le bouton CTA
   */
  updateButtonPrice() {
    if (!this.buttonPriceSpan) return;

    const selectedPurchaseType = this.container.querySelector('[name^="purchase-type-"]:checked');
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    const variantPrice = parseInt(selectedVariant?.dataset.variantPrice) || 0;
    const variantComparePrice = parseInt(selectedVariant?.dataset.variantComparePrice) || 0;
    let priceToShow = 0;

    if (selectedPurchaseType?.value === 'subscription') {
      // Abonnement : IMPORTANT - appliquer d'abord les réductions variantes/sova days, puis la réduction du selling plan
      const selectedPlan = this.container.querySelector('[name^="selling-plan-"]:checked');
      const discountPercent = parseInt(selectedPlan?.dataset.discountPercent) || 0;
      const oneTimePrice = this.applySovaDaysDiscount(variantPrice);
      priceToShow = Math.round(oneTimePrice * (1 - discountPercent / 100));
    } else {
      // Achat unique (par défaut si aucun type sélectionné ou si one-time sélectionné)
      priceToShow = this.applySovaDaysDiscount(variantPrice);
    }

    if (priceToShow > 0) {
      const formattedPrice = this.formatPrice(priceToShow);
      this.buttonPriceSpan.textContent = formattedPrice;

      // Gérer l'affichage du compare price
      let formattedComparePrice = null;
      const hasSovaDaysFixed = this.getVariantSovaDaysReductionPrice() > 0;
      if (this.buttonComparePriceSpan) {
        if (hasSovaDaysFixed) {
          // sova_days_reduction_price : barrer le price (pas compare_at_price)
          formattedComparePrice = this.formatPrice(variantPrice);
          this.buttonComparePriceSpan.innerHTML = `&nbsp;<s>${formattedComparePrice}</s>`;
          this.buttonComparePriceSpan.style.display = '';
        } else if (variantComparePrice && variantComparePrice > priceToShow) {
          formattedComparePrice = this.formatPrice(variantComparePrice);
          this.buttonComparePriceSpan.innerHTML = `&nbsp;<s>${formattedComparePrice}</s>`;
          this.buttonComparePriceSpan.style.display = '';
        } else if (priceToShow < variantPrice) {
          formattedComparePrice = this.formatPrice(variantPrice);
          this.buttonComparePriceSpan.innerHTML = `&nbsp;<s>${formattedComparePrice}</s>`;
          this.buttonComparePriceSpan.style.display = '';
        } else {
          this.buttonComparePriceSpan.style.display = 'none';
        }
      }

      // Mettre à jour aussi le sticky button avec le même compare price
      this.updateStickyButton(null, formattedPrice, formattedComparePrice);
    }
  }

  /**
   * Gestion de la soumission du formulaire
   */
  async handleSubmit(e) {
    e.preventDefault();

    // Désactiver le bouton pendant le traitement
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('product-form__submit--loading');
    }

    // Masquer les erreurs précédentes
    this.hideError();

    try {
      // Récupérer les données du formulaire
      const formData = new FormData(this.form);

      // Nettoyer les properties vides de gift card
      const keysToCheck = ['properties[Message]', 'properties[Recipient email]', 'properties[Recipient name]'];
      keysToCheck.forEach(key => {
        if (formData.has(key)) {
          const value = formData.get(key);
          if (!value || value.trim() === '') {
            formData.delete(key);
          }
        }
      });

      // Si __shopify_send_gift_card_to_recipient est false, supprimer les properties de destinataire
      if (formData.get('properties[__shopify_send_gift_card_to_recipient]') === 'false') {
        formData.delete('properties[__shopify_send_gift_card_to_recipient]');
        formData.delete('properties[Recipient email]');
        formData.delete('properties[Recipient name]');
      }

      // Ajouter au panier via l'API Shopify
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erreur lors de l\'ajout au panier');
      }

      const item = await response.json();

      // Note: Les événements pour les apps tierces (Dialog, Klaviyo, etc.)
      // sont dispatchés globalement par l'intercepteur fetch dans theme.liquid
      // Cela assure que TOUS les ajouts au panier déclenchent les événements

      // Publier sur le PubSub Shopify si disponible (pour les apps Shopify natives)
      if (typeof window.Shopify !== 'undefined' && window.Shopify.publish) {
        window.Shopify.publish('cart:item-added', { item });
      }

      // Rafraîchir et ouvrir le cart drawer
      const cartDrawer = document.querySelector('apt-cart-drawer');
      if (cartDrawer) {
        // Rafraîchir le contenu du panier
        if (typeof cartDrawer.refresh === 'function') {
          await cartDrawer.refresh();
        } else {
          // Dispatcher l'événement refresh pour forcer le rafraîchissement
          cartDrawer.dispatchEvent(new CustomEvent('refresh'));
        }

        // Petit délai pour laisser le temps au refresh de se terminer
        await new Promise(resolve => setTimeout(resolve, 100));

        // Ouvrir le drawer
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('active');
        }
      }

      // Fermer la modal quick-buy si on est dedans
      const quickBuyModal = this.container.closest('apt-quick-buy-drawer');
      if (quickBuyModal && typeof quickBuyModal.close === 'function') {
        quickBuyModal.close();
        console.log('🔒 Quick buy modal closed after add to cart');
      }

      // Rafraîchir le nombre d'articles dans le panier
      this.refreshCartCount();

      // Feedback visuel de succès
      this.showSuccess();

    } catch (error) {
      this.showError(error.message);
    } finally {
      // Réactiver le bouton
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('product-form__submit--loading');
      }
    }
  }

  /**
   * Rafraîchit le compteur du panier
   */
  async refreshCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Mettre à jour tous les compteurs de panier
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Note: L'événement cart:updated est dispatché par l'intercepteur global dans theme.liquid
    } catch (error) {
      console.error('Erreur lors de la mise à jour du panier:', error);
    }
  }

  /**
   * Affiche un message de succès
   */
  showSuccess() {
    if (this.submitButton) {
      const originalText = this.submitButton.innerHTML;
      this.submitButton.innerHTML = '<span>✓ AJOUTÉ AU PANIER</span>';
      this.submitButton.classList.add('product-form__submit--success');

      setTimeout(() => {
        this.submitButton.innerHTML = originalText;
        this.submitButton.classList.remove('product-form__submit--success');
      }, 2000);
    }
  }

  /**
   * Affiche un message d'erreur
   */
  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
    }
  }

  /**
   * Masque le message d'erreur
   */
  hideError() {
    if (this.errorMessage) {
      this.errorMessage.style.display = 'none';
      this.errorMessage.textContent = '';
    }
  }

  /**
   * Met à jour le protocol header : swap la liste d'items + prix total
   * Prend en compte le type d'achat (one-time vs subscription)
   */
  updateProtocolDisplay(variantId, variantPrice, variantComparePrice) {
    if (!this.protocolHeader) return;

    // Swap items list
    if (variantId && this.protocolItemsWrapper) {
      this.protocolItemsWrapper.querySelectorAll('[data-protocol-items-for-variant]').forEach(el => {
        el.hidden = el.dataset.protocolItemsForVariant !== String(variantId);
      });
    }

    // Déterminer le prix effectif selon le type d'achat
    const isSubscription = this.container.querySelector('[name^="purchase-type-"]:checked')?.value === 'subscription';
    let effectivePrice = variantPrice;

    if (isSubscription) {
      const selectedPlan = this.container.querySelector('[name^="selling-plan-"]:checked');
      if (selectedPlan) {
        const discountPercent = parseInt(selectedPlan.dataset.discountPercent) || 0;
        const oneTimePrice = this.applySovaDaysDiscount(variantPrice);
        effectivePrice = Math.round(oneTimePrice * (1 - discountPercent / 100));
      }
    } else {
      effectivePrice = this.applySovaDaysDiscount(variantPrice);
    }

    // Le compare price reste toujours le prix original (sans réduction)
    const comparePrice = variantComparePrice > 0 ? variantComparePrice : variantPrice;

    // Update main price
    if (this.protocolPrice) {
      this.protocolPrice.textContent = this.formatPrice(effectivePrice);
    }

    // Update compare price, discount %, savings
    const hasCompare = comparePrice > effectivePrice;

    if (this.protocolPriceDetails) {
      this.protocolPriceDetails.hidden = !hasCompare;
    }
    if (this.protocolSavingsBadge) {
      this.protocolSavingsBadge.hidden = !hasCompare;
    }

    if (hasCompare) {
      const savingsPct = Math.round((comparePrice - effectivePrice) / comparePrice * 100);
      const savingsAmount = comparePrice - effectivePrice;

      if (this.protocolComparePrice) {
        this.protocolComparePrice.textContent = this.formatPrice(comparePrice);
      }
      if (this.protocolDiscount) {
        this.protocolDiscount.textContent = `${this.protocolDiscountPrefix} -${savingsPct}%`;
      }
      if (this.protocolSavingsAmount) {
        this.protocolSavingsAmount.textContent = `-${this.formatPrice(savingsAmount)}`;
      }
    }
  }

  /**
   * Rafraîchit le prix protocol sans changer la variante (ex: switch one-time/subscription)
   */
  refreshProtocolPrice() {
    if (!this.protocolHeader) return;
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    if (!selectedVariant) return;
    const variantPrice = parseInt(selectedVariant.dataset.variantPrice);
    const variantComparePrice = parseInt(selectedVariant.dataset.variantComparePrice) || 0;
    this.updateProtocolDisplay(null, variantPrice, variantComparePrice);
  }

  /**
   * Formate un prix en centimes avec la devise du site
   */
  formatPrice(priceInCents) {
    const amount = priceInCents / 100;
    const formatted = amount.toFixed(2).replace(/\.00$/, '');

    // Récupérer la devise du site
    const currency = window.apotech?.currency || 'EUR';

    // Symboles de devises
    const currencySymbols = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'CHF': 'CHF',
      'CAD': 'CA$'
    };

    const symbol = currencySymbols[currency] || currency;

    // Format selon la locale
    const localizedAmount = currency === 'EUR'
      ? formatted.replace('.', ',')
      : formatted;

    return `${symbol}${localizedAmount}`;
  }

  /**
   * Récupère le pourcentage de réduction de la variante courante (Opération Promotionnelle)
   * Utilise uniquement la réduction de base, sans système de paliers
   */
  getVariantOpePromoDiscount() {
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    if (!selectedVariant || !this.opePromoEnabled) {
      return 0;
    }

    // Récupérer la réduction de base depuis les data attributes
    const reduction = parseFloat(selectedVariant.dataset.variantOpePromoDiscount) || 0;

    console.log('=== OPE PROMO DISCOUNT ===');
    console.log('Variant has ope_promo:', reduction > 0);
    console.log('Discount:', reduction + '%');
    console.log('==========================');

    return reduction;
  }

  /**
   * Récupère le prix sova_days_reduction_price de la variante courante (metafield)
   * @returns {number} Prix en centimes, ou 0 si pas de metafield
   */
  getVariantSovaDaysReductionPrice() {
    const selectedVariant = this.container.querySelector('[name^="variant-"]:checked');
    if (!selectedVariant) return 0;

    const sovaDaysPrice = selectedVariant.dataset.variantSovaDaysPrice;
    if (sovaDaysPrice && sovaDaysPrice !== '') {
      // Le metafield est en euros (ex: "45.50"), convertir en centimes
      return Math.round(parseFloat(sovaDaysPrice) * 100);
    }
    return 0;
  }

  /**
   * Applique les réductions (sova_days_reduction_price > Opération Promotionnelle > Sova Days global)
   */
  applySovaDaysDiscount(priceInCents) {
    // Priorité 0 : sova_days_reduction_price (metafield variante = prix fixe)
    const sovaDaysReductionPrice = this.getVariantSovaDaysReductionPrice();
    if (sovaDaysReductionPrice > 0) {
      return sovaDaysReductionPrice;
    }

    // Priorité 1 : Opération Promotionnelle
    const variantOpePromoDiscount = this.getVariantOpePromoDiscount();

    if (variantOpePromoDiscount > 0) {
      const discountMultiplier = 1 - (variantOpePromoDiscount / 100);
      const discountedPrice = Math.round(priceInCents * discountMultiplier);
      return Math.max(0, discountedPrice);
    }

    // Priorité 2 : Sova Days global
    if (!this.sovaDaysEnabled) {
      return priceInCents;
    }

    const totalDiscountPercent = this.sovaDaysDiscount + this.sovaDaysAdditionalAmount;

    if (totalDiscountPercent <= 0) {
      return priceInCents;
    }

    const discountMultiplier = 1 - (totalDiscountPercent / 100);
    const discountedPrice = Math.round(priceInCents * discountMultiplier);

    return Math.max(0, discountedPrice);
  }

  /**
   * Remplace [price] par le plus petit prix divisé par 3
   */
  replacePricePlaceholder() {
    const deliveryInfoItems = this.container.querySelectorAll('.product-form__delivery-info-item');
    if (deliveryInfoItems.length === 0) return;

    // Récupérer tous les prix des variantes
    const prices = [];
    this.variantRadios.forEach(radio => {
      const price = parseInt(radio.dataset.variantPrice);
      if (!isNaN(price) && price > 0) {
        prices.push(price);
      }
    });

    if (prices.length === 0) return;

    // Trouver le plus petit prix
    const minPrice = Math.min(...prices);

    // Appliquer la réduction Sova Days
    const discountedMinPrice = this.applySovaDaysDiscount(minPrice);

    // Diviser par 3 et arrondir
    const priceDiv3 = Math.round(discountedMinPrice / 3);

    // Formater le prix
    const formattedPrice = this.formatPrice(priceDiv3);

    // Remplacer [price] dans tous les éléments
    deliveryInfoItems.forEach(item => {
      const textElements = item.querySelectorAll('p, strong, span');
      textElements.forEach(el => {
        if (el.innerHTML.includes('[price]')) {
          el.innerHTML = el.innerHTML.replace(/\[price\]/g, formattedPrice);
        }
      });
    });
  }

  /**
   * Initialise le sticky button qui apparaît en bas de la page sur mobile
   * jusqu'à ce que le vrai bouton soit visible
   */
  initStickyButton() {
    if (!this.submitButton || !this.stickyCtaContainer || !this.stickySubmitButton) return;

    // Click sur le sticky button déclenche le vrai bouton
    this.stickySubmitButton.addEventListener('click', () => {
      this.submitButton.click();
    });

    // Intersection Observer pour détecter quand le vrai bouton est visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Le vrai bouton est visible, cacher le sticky button
          this.stickyCtaContainer.classList.remove('product-form__sticky-cta--active');
        } else {
          // Le vrai bouton n'est pas visible, afficher le sticky button
          this.stickyCtaContainer.classList.add('product-form__sticky-cta--active');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px'
    });

    // Observer le vrai bouton
    observer.observe(this.submitButton);
  }

  /**
   * Met à jour le contenu du sticky button (label, prix et compare price)
   */
  updateStickyButton(label, price, comparePrice = null) {
    if (!this.stickyLabel || !this.stickyPrice) return;

    if (label) {
      this.stickyLabel.textContent = label;
    }

    if (price) {
      this.stickyPrice.textContent = price;
    }

    // Gérer l'affichage du compare price dans le sticky button
    if (this.stickyComparePrice) {
      if (comparePrice) {
        this.stickyComparePrice.innerHTML = `&nbsp;<s>${comparePrice}</s>`;
        this.stickyComparePrice.style.display = '';
      } else {
        this.stickyComparePrice.style.display = 'none';
      }
    }
  }
}

/**
 * Calcule la date de livraison en jours ouvrés
 */
function calculateDeliveryDate(businessDays) {
  let date = new Date();
  let daysAdded = 0;

  while (daysAdded < businessDays) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();

    // Ignorer samedi (6) et dimanche (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }

  return date;
}

/**
 * Formate une date selon la langue
 */
function formatDeliveryDate(date, language = 'fr') {
  const translations = {
    fr: {
      dayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      monthNames: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
    },
    en: {
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    },
    it: {
      dayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
      monthNames: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre']
    },
    de: {
      dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    },
    es: {
      dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
      monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
    }
  };

  const lang = translations[language] || translations['fr'];
  const dayName = lang.dayNames[date.getDay()];
  const day = date.getDate();
  const month = lang.monthNames[date.getMonth()];

  return `${dayName} ${day} ${month}`;
}

/**
 * Initialise les dates de livraison
 */
function initDeliveryDate() {
  const deliveryElements = document.querySelectorAll('[data-delivery-date]');

  deliveryElements.forEach(element => {
    const businessDays = parseInt(element.dataset.deliveryDays) || 5;
    const language = element.dataset.deliveryLanguage || 'fr';
    const deliveryDate = calculateDeliveryDate(businessDays);
    const formattedDate = formatDeliveryDate(deliveryDate, language);
    element.textContent = formattedDate;
  });
}

/**
 * Description courte — toggle "Voir plus / Voir moins" via CSS line-clamp.
 */
function setupDescriptionTruncate(container) {
  if (container.dataset.truncateInitialized === 'true') return;
  container.dataset.truncateInitialized = 'true';

  const toggleBtn = container.querySelector('[data-description-toggle]');
  if (!toggleBtn) return;

  const seeMore = toggleBtn.textContent.trim();
  const seeLess = toggleBtn.dataset.labelLess || 'Voir moins';

  toggleBtn.addEventListener('click', () => {
    const expanded = container.classList.toggle('product-form__description--expanded');
    container.classList.toggle('product-form__description--clamped', !expanded);
    toggleBtn.textContent = expanded ? seeLess : seeMore;
  });
}

function initDescriptionTruncate(root = document) {
  root.querySelectorAll('[data-description-toggleable]').forEach(setupDescriptionTruncate);
}

/**
 * Initialisation
 */
const productFormInstances = [];

function initProductForms() {
  const productForms = document.querySelectorAll('.product-form');

  productForms.forEach(form => {
    const instance = new ProductFormV2(form);
    productFormInstances.push(instance);
  });

  initDeliveryDate();
  initDescriptionTruncate();
}

// Lancer l'initialisation
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductForms);
} else {
  initProductForms();
}

// Support de l'éditeur de thème Shopify
if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const productForms = event.target.querySelectorAll('.product-form');
    productForms.forEach(form => new ProductFormV2(form));
    initDescriptionTruncate(event.target);
  });
}

// Exporter ProductFormV2 globalement pour utilisation dans quick-buy-modal
window.ProductFormV2 = ProductFormV2;
